import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Camera } from "lucide-react";

/**
 * PhotoGallery — responsive masonry-ish grid of images with a fullscreen
 * lightbox viewer. Designed to accompany event/photo-heavy articles.
 */
const PhotoGallery = ({ images = [], title = "Photo Album" }) => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const open = (i) => setLightboxIndex(i);
  const close = () => setLightboxIndex(null);
  const prev = useCallback(
    () => setLightboxIndex((i) => (i === null ? null : (i - 1 + images.length) % images.length)),
    [images.length]
  );
  const next = useCallback(
    () => setLightboxIndex((i) => (i === null ? null : (i + 1) % images.length)),
    [images.length]
  );

  // Keyboard shortcuts + body scroll lock while lightbox open
  useEffect(() => {
    if (lightboxIndex === null) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightboxIndex, prev, next]);

  if (!images || images.length === 0) return null;

  return (
    <section className="mt-16 pt-12 border-t border-border" data-testid="photo-gallery">
      <div className="flex items-center gap-3 mb-8">
        <Camera className="h-4 w-4 text-primary" />
        <span
          className="font-mono text-xs uppercase tracking-[0.3em] text-primary"
          data-testid="gallery-label"
        >
          {title}
        </span>
        <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground ml-auto">
          {images.length} shot{images.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
        {images.map((src, idx) => (
          <button
            key={`${src}-${idx}`}
            type="button"
            onClick={() => open(idx)}
            className="relative aspect-square overflow-hidden bg-muted group focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid={`gallery-thumb-${idx}`}
          >
            <img
              src={src}
              alt={`${title} #${idx + 1}`}
              loading="lazy"
              className="w-full h-full object-cover img-grayscale group-hover:grayscale-0 group-hover:scale-[1.05] transition-all duration-500"
            />
            <div className="absolute inset-0 border border-transparent group-hover:border-primary/50 transition-colors" />
            <span className="absolute bottom-1 right-2 font-mono text-[10px] text-white/80 opacity-0 group-hover:opacity-100 transition-opacity">
              {String(idx + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
            </span>
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center"
          onClick={close}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={close}
            className="absolute top-6 right-6 text-white/70 hover:text-white p-2"
            aria-label="Close"
            data-testid="gallery-close"
          >
            <X className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 bg-white/5 hover:bg-white/10 rounded-full"
            aria-label="Previous"
            data-testid="gallery-prev"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 bg-white/5 hover:bg-white/10 rounded-full"
            aria-label="Next"
            data-testid="gallery-next"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
          <div
            className="max-w-[92vw] max-h-[90vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[lightboxIndex]}
              alt={`${title} #${lightboxIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain"
            />
            <div className="mt-4 font-mono text-xs uppercase tracking-widest text-white/60">
              {String(lightboxIndex + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PhotoGallery;
